import React, { useEffect, useState, useRef } from 'react';
import { CSVLink } from 'react-csv';
import DataTable from 'react-data-table-component';
import { DatePicker, Tooltip, Space, Tag, Select, Popconfirm, Modal, Input, Button } from 'antd';
import PropagateLoader from "react-spinners/PropagateLoader";
import { getUTCToLocalDateTimeFormat, toDayDate, queryDate } from '../../../Utils/function';
import { getRequest, postRequest, downloadRequest } from './../../../Utils/AppApi';
import { useHistory } from "react-router-dom";
import { toast } from 'react-toastify';
import {
    GET_FUND_TRANSFER_CHECKER_LIST,
    POST_FUND_TRANSFER_CHECKER_BULK_ACTION,
    POST_FUND_TRANSFER_BULK_REFUND,
    POST_FUND_TRANSFER_MANUAL_REFUND
} from './../../../Utils/appConstants';
import { INRFormat } from './../../../Utils/Common';

function CheckerDashboard() {
    const history = useHistory();
    const csvLink = useRef();
    const limit = 10;
    const dateFormat = 'DD-MM-YYYY';
    const [isLoading, setLoading] = useState(false);
    const [endDate, setEndDate] = useState(toDayDate());
    const [startDate, setStartDate] = useState(toDayDate());
    const [getData, setData] = useState([]);
    const [getTotalRecord, setTotalRecord] = useState(0);
    const [page, setPage] = useState(1);
    const [isLimit, setLimit] = useState(limit);
    const [searchCustomerId, setSearchCustomerId] = useState(null);
    const [searchTransId, setSearchTransId] = useState(null);
    const [searchStatus, setSearchStatus] = useState('pending_checker');
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [csvData, setCsvData] = useState([]);

    // Modals
    const [refundModalVisible, setRefundModalVisible] = useState(false);
    const [refundRequestId, setRefundRequestId] = useState(null);
    const [refundRemark, setRefundRemark] = useState('');
    const [bulkRefundModalVisible, setBulkRefundModalVisible] = useState(false);

    const objectToQueryString = (obj) => {
        var str = [];
        for (var p in obj)
            if (obj.hasOwnProperty(p)) {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        return str.join("&");
    };

    const params = { page: page, limit: isLimit, status: searchStatus };

    const getDataReports = (data) => {
        if (data) {
            setLimit(data);
            params.limit = data;
        }
        if (startDate && endDate) {
            params.startDate = startDate ? queryDate(startDate) : "";
            params.endDate = endDate ? queryDate(endDate) : "";
        }
        params.searchCustomerId = searchCustomerId ? searchCustomerId : '';
        params.searchTransId = searchTransId ? searchTransId : '';

        const queryString = objectToQueryString(params);
        setLoading(true);
        getRequest(`${GET_FUND_TRANSFER_CHECKER_LIST}?${queryString}`, history).then((response) => {
            if (response.success === true) {
                setData(response.data);
                setTotalRecord(response.data.totalDocs);
                setPage(response.data.page);
            } else {
                setData([]);
                setTotalRecord(0);
                setPage(1);
            }
            setLoading(false);
        }).catch(function (error) {
            console.log(error);
            setLoading(false);
        });
    };

    // ---- CSV Export ----
    const getCSVDataList = () => {
        let params = {};
        params.exportsCsv = true;
        params.status = searchStatus;
        if (startDate && endDate) {
            params.startDate = startDate ? queryDate(startDate) : "";
            params.endDate = endDate ? queryDate(endDate) : "";
        }
        params.searchCustomerId = searchCustomerId ? searchCustomerId : '';
        params.searchTransId = searchTransId ? searchTransId : '';

        const queryString = objectToQueryString(params);
        getRequest(`${GET_FUND_TRANSFER_CHECKER_LIST}?${queryString}`, history).then((response) => {
            if (response.success === true) {
                const records = response.data;
                const csvRows = records.map(row => ({
                    'Date Time': getUTCToLocalDateTimeFormat(row.createdAt),
                    'Transaction ID': row.transactionId,
                    'Amount': INRFormat(row.amount),
                    'Status': row.finalStatus,
                    'Mode': 'NEFT',
                    'Beneficiary': row.baneName,
                    'Account Number': row.accountNumber,
                    'IFSC': row.ifsccode,
                    'Requested By': row.requester?.username || '',
                }));
                setCsvData(csvRows);
                setTimeout(() => {
                    csvLink.current.link.click();
                }, 500);
            } else {
                setCsvData([]);
                toast.error('No data to export');
            }
        }).catch(function (error) {
            console.log(error);
        });
    };

    // ---- Helper: Format date as YYYY-MM-DD ----
    const formatDateForAPI = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // ---- A2A DFT Export ----
    const handleExportA2A = async (status = null) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', formatDateForAPI(startDate));
            if (endDate) params.append('endDate', formatDateForAPI(endDate));
            if (status && typeof status === 'string' && status.trim() !== '') {
                params.append('status', status.trim());
            }

            const url = `/fund-transfer/export-a2a-dft?${params.toString()}`;
            console.log('📤 A2A Export URL:', url);

            const response = await downloadRequest(url, history);
            const blob = response.data;

            if (!blob || blob.size === 0) {
                throw new Error('Received empty file from server');
            }

            let filename = 'A2A_DFT.xls';
            const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/i);
                if (match && match[1]) {
                    filename = match[1];
                }
            }

            if (filename.includes('.xlsx') || filename === 'A2A_DFT.xls') {
                const now = new Date();
                const pad = (n) => String(n).padStart(2, '0');
                const dateTimeStr = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
                filename = `SRPAY${dateTimeStr}.xls`;
            }

            console.log('📄 Final Filename:', filename);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);

            toast.success('✅ A2A DFT file downloaded successfully (.xls)');
        } catch (error) {
            console.error('💥 Export error:', error);
            toast.error(error.message || 'Failed to download A2A DFT');
        } finally {
            setLoading(false);
        }
    };

    // ---- SINGLE ROW ACTION ----
    const handleSingleAction = (requestId, action) => {
        setLoading(true);
        const payload = { requestIds: [requestId], action };
        postRequest(`${POST_FUND_TRANSFER_CHECKER_BULK_ACTION}`, payload, history)
            .then((response) => {
                if (response.success) {
                    const data = response.data || [];
                    if (data.length > 0) {
                        const item = data[0];
                        if (item.status === 'success') {
                            toast.success(item.message);
                        } else {
                            toast.warning(item.message);
                        }
                    } else {
                        toast.success(response.message);
                    }
                    getDataReports();
                } else {
                    toast.error(response.message);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setLoading(false);
            });
    };

    // ---- BULK ACTION ----
    const handleBulkAction = (action) => {
        if (selectedRows.length === 0) {
            toast.warning('Please select at least one request.');
            return;
        }
        const requestIds = selectedRows.map(row => row._id);
        setLoading(true);
        const payload = { requestIds, action };
        postRequest(`${POST_FUND_TRANSFER_CHECKER_BULK_ACTION}`, payload, history)
            .then((response) => {
                if (response.success) {
                    const data = response.data || [];
                    const successCount = data.filter(item => item.status === 'success').length;
                    const skippedCount = data.filter(item => item.status === 'skipped').length;
                    const failedCount = data.filter(item => item.status === 'failed').length;

                    let message = `✅ ${successCount} transaction(s) processed successfully.`;
                    if (skippedCount > 0) {
                        const skippedMessages = data
                            .filter(item => item.status === 'skipped')
                            .map(item => `• ${item.id}: ${item.message}`)
                            .join('\n');
                        message += `\n⚠️ ${skippedCount} skipped:\n${skippedMessages}`;
                    }
                    if (failedCount > 0) {
                        const failedMessages = data
                            .filter(item => item.status === 'failed')
                            .map(item => `• ${item.id}: ${item.message}`)
                            .join('\n');
                        message += `\n❌ ${failedCount} failed:\n${failedMessages}`;
                    }
                    toast.info(message, { autoClose: 10000 });
                    setSelectedRows([]);
                    setToggleCleared(!toggleCleared);
                    getDataReports();
                } else {
                    toast.error(response.message);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setLoading(false);
            });
    };

    // ---- Manual Refund ----
    const openRefundModal = (requestId) => {
        setRefundRequestId(requestId);
        setRefundRemark('');
        setRefundModalVisible(true);
    };

    const handleManualRefund = () => {
        if (!refundRequestId) return;
        setLoading(true);
        const payload = { requestId: refundRequestId, remark: refundRemark };
        postRequest(`${POST_FUND_TRANSFER_MANUAL_REFUND}`, payload, history)
            .then((response) => {
                if (response.success) {
                    toast.success(response.message);
                    setRefundModalVisible(false);
                    getDataReports();
                } else {
                    toast.error(response.message);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setLoading(false);
            });
    };

    // ---- Bulk Refund ----
    const handleBulkRefund = () => {
        if (selectedRows.length === 0) {
            toast.warning('Please select at least one failed request.');
            return;
        }
        const requestIds = selectedRows.map(row => row._id);
        setLoading(true);
        const payload = { requestIds, remark: 'Bulk refund' };
        postRequest(`${POST_FUND_TRANSFER_BULK_REFUND}`, payload, history)
            .then((response) => {
                if (response.success) {
                    toast.success(response.message);
                    setSelectedRows([]);
                    setToggleCleared(!toggleCleared);
                    setBulkRefundModalVisible(false);
                    getDataReports();
                } else {
                    toast.error(response.message);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setLoading(false);
            });
    };

    const handlePageChange = (page, size) => { setPage(page); };
    const handleSearch = () => { getDataReports(); };
    const handleClear = () => {
        setStartDate('');
        setEndDate('');
        setSearchCustomerId('');
        setSearchTransId('');
        setSearchStatus('pending_checker');
        handleSearch();
    };

    useEffect(() => {
        getDataReports();
    }, [page]);

    // ---- Columns ----
    const columns = [
        {
            name: 'Date Time',
            cell: row => getUTCToLocalDateTimeFormat(row.createdAt),
            width: "200px",
        },
        {
            name: 'Trans ID',
            selector: row => <Tooltip placement="bottom" title={row.transactionId}>{row.transactionId}</Tooltip>,
            width: "180px",
        },
        {
            name: 'User Id/ Name',
            selector: row =>
                `${row.requester?.username || '-'} / ${row.requester?.name || '-'}`,
            width: "200px"
        },
        {
            name: 'Amount',
            selector: row => INRFormat(row.amount),
            width: "100px",
        },
        {
            name: 'Final Status',
            selector: row => {
                const statusMap = {
                    'submitted': 'Submitted',
                    'in_process': 'In Process',
                    'sent_to_bank': 'Sent to Bank',
                    'pending_at_bank': 'Pending at Bank',
                    'success': 'Success',
                    'failed': 'Failed',
                    'refunded': 'Refunded'
                };
                return statusMap[row.finalStatus] || row.finalStatus || 'Unknown';
            },
        },
        {
            name: 'Mode',
            selector: () => 'NEFT',
        },
        {
            name: 'Txn Type',
            selector: row => row.txnType || '-',
            width: "180px"
        },
        {
            name: 'Mobile',
            selector: row => row.customerMobile || '-',
        },
        {
            name: 'IFSC',
            selector: row => row.ifsccode || '-',
        },
        {
            name: 'Account',
            selector: row => row.accountNumber || '-',
        },
        {
            name: 'Beneficiary',
            selector: row => row.baneName || '-',
        },
        {
            name: 'Action',
            cell: row => {
                if (row.workflowStatus === 'pending_checker') {
                    return (
                        <Space size="middle">
                            <Popconfirm
                                placement="top"
                                title="Accept this request?"
                                onConfirm={() => handleSingleAction(row._id, 'accept')}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Tag color="blue" className="pointer">Accept</Tag>
                            </Popconfirm>
                            <Popconfirm
                                placement="top"
                                title="Approve (Send to Bank)?"
                                onConfirm={() => handleSingleAction(row._id, 'approve')}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Tag color="green" className="pointer">Approve</Tag>
                            </Popconfirm>
                            <Popconfirm
                                placement="top"
                                title="Reject this request?"
                                onConfirm={() => handleSingleAction(row._id, 'reject')}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Tag color="red" className="pointer">Reject</Tag>
                            </Popconfirm>
                        </Space>
                    );
                } else if (row.workflowStatus === 'failed' && !row.isRefund) {
                    return (
                        <Tag color="orange" className="pointer" onClick={() => openRefundModal(row._id)}>
                            Manual Refund
                        </Tag>
                    );
                } else if (row.workflowStatus === 'refunded' || row.isRefund) {
                    return <Tag color="grey">Refunded</Tag>;
                } else {
                    return <Tag color="grey">Processed</Tag>;
                }
            },
            width: "220px",
        }
    ];

    // ---- Row selection config ----
    const rowSelection = {
        selectedRows: selectedRows,
        onSelectedRowsChange: ({ selectedRows }) => {
            setSelectedRows(selectedRows);
        },
    };

    if (isLoading) {
        return <div className="content text-center" style={{ margin: "250px 0" }}>
            <PropagateLoader color="#5851D2" loading={isLoading} size={25} />
        </div>;
    }

    return (
        <div>
            <div className="content-wrapper pt-2">
                <div className="content-header my-0 py-0">
                    <div className="container-fluid">
                        <div className="row mb-0 align-items-center">
                            <div className="col-sm-6">
                                <h1 className='page-title text-blue font-merriweather'>Level 1 <span className='text-red'></span></h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="content">
                    <div className="container-fluid pt-2 pb-5">
                        <div className="row">
                            <div className='card col-md-12 py-3'>
                                <div className='card-body p-0'>
                                    <div className="row">
                                        <div className='col-md-12 date-search mb-0'>
                                            <div className='row'>
                                                <div className='col-md-3 col-lg-3 d-flex'>
                                                    <div className="form-group mr-3">
                                                        <label className='date-search-label mb-0 text-blue'>From</label>
                                                        <DatePicker className="form-control form-control-border" value={startDate} format={dateFormat} onChange={(date) => setStartDate(date)} />
                                                    </div>
                                                    <div className="form-group">
                                                        <label className='date-search-label mb-0 text-red'>To</label>
                                                        <DatePicker className="form-control form-control-border" value={endDate} format={dateFormat} onChange={(date) => setEndDate(date)} />
                                                    </div>
                                                </div>
                                                <div className='col-md-3 col-lg-3 d-flex'>
                                                    <div className="form-group mr-3">
                                                        <label className='date-search-label mb-0 text-red'></label>
                                                        <input type="text" className="form-control form-control-border" placeholder="Trans ID" onChange={(e) => setSearchTransId(e.target.value)} value={searchTransId} />
                                                    </div>
                                                </div>
                                                <div className='col-md-3 col-lg-3 d-flex'>
                                                    <div className="form-group col-6">
                                                        <label className='date-search-label mb-0 text-red'>&nbsp;</label>
                                                        <Select
                                                            className="w-100"
                                                            value={searchStatus}
                                                            onChange={(value) => setSearchStatus(value)}
                                                            options={[
                                                                { value: 'pending_checker', label: 'Pending' },
                                                                { value: 'pending_maker', label: 'In Process' },
                                                                { value: 'sent_to_bank', label: 'Sent to Bank' },
                                                            ]}
                                                            placeholder="-- Status --"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='col-md-12 date-search mb-0'>
                                            <div className='row'>
                                                <div className='col-md-5'></div>
                                                <div className='col-md-1'>
                                                    <button type="submit" className="btn btn-primary rounded-pill px-3" onClick={() => handleSearch()}>Search</button>
                                                </div>
                                                <div className='col-md-1'>
                                                    <button type="submit" className="btn btn-default rounded-pill px-4" onClick={() => handleClear()}>Clear</button>
                                                </div>
                                                <div className='col-md-5'>
                                                    <button type="button" className="btn btn-primary mr-2" onClick={() => getCSVDataList()}>
                                                        <i className="fa fa-file-excel"></i> Export CSV
                                                    </button>
                                                    <CSVLink
                                                        data={csvData}
                                                        filename='checker_fund_transfer_list'
                                                        className='hidden'
                                                        ref={csvLink}
                                                        target='_blank'
                                                    />
                                                    <button type="button" className="btn btn-warning" onClick={() => handleExportA2A('in_process')}>
                                                        <i className="fa fa-file-excel"></i> A2A DFT
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="row mt-3 mb-3">
                                        <div className="col-md-12">
                                            <Button type="primary" className="btn btn-success rounded-pill px-3 mr-2" onClick={() => handleBulkAction('accept')}>Mark Accepted</Button>
                                            <Button type="primary" className="btn btn-info rounded-pill px-3 mr-2" onClick={() => handleBulkAction('approve')}>Mark Approved</Button>
                                            <Button type="danger" className="btn btn-danger rounded-pill px-3 mr-2" onClick={() => handleBulkAction('reject')}>Mark Rejected</Button>
                                            <span className="ml-3 text-muted">Selected: {selectedRows.length}</span>
                                        </div>
                                    </div>

                                    <div className="container-fluid pt-2 pb-5">
                                        <DataTable
                                            title={""}
                                            columns={columns}
                                            data={getData.docs}
                                            highlightOnHover
                                            pagination
                                            paginationServer
                                            selectableRows
                                            selectableRowsHighlight
                                            selectableRowDisabled={row => 
                                                row.finalStatus === 'success' || 
                                                row.finalStatus === 'failed' || 
                                                row.finalStatus === 'refunded'
                                            }
                                            onSelectedRowsChange={rowSelection.onSelectedRowsChange}
                                            clearSelectedRows={toggleCleared}
                                            paginationTotalRows={getTotalRecord}
                                            onChangeRowsPerPage={getDataReports}
                                            onChangePage={handlePageChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <Modal
                title="Manual Refund"
                visible={refundModalVisible}
                onOk={handleManualRefund}
                onCancel={() => setRefundModalVisible(false)}
                okText="Refund"
                cancelText="Cancel"
                confirmLoading={isLoading}
            >
                <p>Request ID: <strong>{refundRequestId}</strong></p>
                <div className="form-group">
                    <label>Remark (optional)</label>
                    <Input.TextArea
                        rows={3}
                        value={refundRemark}
                        onChange={(e) => setRefundRemark(e.target.value)}
                        placeholder="Enter refund remark"
                    />
                </div>
                <div className="alert alert-warning">
                    <strong>Warning:</strong> This will credit the user's wallet and reverse commissions.
                </div>
            </Modal>

            <Modal
                title="Bulk Refund"
                visible={bulkRefundModalVisible}
                onOk={handleBulkRefund}
                onCancel={() => setBulkRefundModalVisible(false)}
                okText="Refund All Selected"
                cancelText="Cancel"
                confirmLoading={isLoading}
            >
                <p>You are about to refund <strong>{selectedRows.length}</strong> selected failed transactions.</p>
                <div className="alert alert-warning">
                    <strong>Warning:</strong> This will credit users' wallets and reverse commissions for all selected.
                </div>
            </Modal>
        </div>
    );
}

export default CheckerDashboard;